import {
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    Row,
    Select,
} from "antd";
import React, { useCallback, useEffect, useMemo } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useLocation } from "react-router-dom";
import { useOperationHook } from "../../../../../hooks/useOperationHook";
import { IActivityList, IAirportCodeList, ICommonState, IMarkenResponse, IPackageSizeList, IPackageTempList, IPackagingList, IShipmentTemperatureList, ITruckTypeList } from "../../../../../model/CommonModel";
import { SessionStorage } from "../../../../../services/storage";
import { useCommonStore } from "../../../../../stores/CommonStore";
import {
    DD_MM_YYYY,
    DD_MM_YYYY_HH_mm,
    dropDownMapping,
    sortBy
} from "../../../../../utils/app-common";
import { convertDateIntoMillis, convertMillisToDateMoment } from "../../../../../utils/luxon";
import { markenHAWBLengthValidation, numericValidation } from "../../../../../validation/validation";
import { calculateDryIceCharge, calculateTotalMarkenCharges } from "../../../../BookingComponent/Booking/common";
import LoadingModalComponent from "../../../../CommonComponent/LoadingModalComponent";
import FileUploadComponent from "./FileUploadComponent";


const optionList = dropDownMapping([
    { id: 1, name: 'JAN' },
    { id: 2, name: 'FEB' },
    { id: 3, name: 'MAR' },
    { id: 4, name: 'APR' },
    { id: 5, name: 'MAY' },
    { id: 6, name: 'JUN' },
    { id: 7, name: 'JUL' },
    { id: 8, name: 'AUG' },
    { id: 9, name: 'SEP' },
    { id: 10, name: 'OCT' },
    { id: 11, name: 'NOV' },
    { id: 12, name: 'DEC' }
], 'id', 'name');

function MarkenFormComponent() {
    const [form] = Form.useForm();
    const { state } = useLocation();
    const activityList: IActivityList[] = useCommonStore((state: ICommonState) => state?.activityList);
    const GetActivityListAction = useCommonStore((state: ICommonState) => state?.GetActivityListAction);
    const airportCodeList: IAirportCodeList[] = useCommonStore((state: ICommonState) => state?.airportCodeList);
    const GetAirportCodeListAction = useCommonStore((state: ICommonState) => state?.GetAirportCodeListAction);
    const shipmentTemperatureList: IShipmentTemperatureList[] = useCommonStore((state: ICommonState) => state?.shipmentTemperatureList);
    const GetShipmentTemperatureListAction = useCommonStore((state: ICommonState) => state?.GetShipmentTemperatureListAction);
    const packageTempList: IPackageTempList[] = useCommonStore((state: ICommonState) => state?.packageTempList);
    const GetPackageTempListAction = useCommonStore((state: ICommonState) => state?.GetPackageTempListAction);
    const packageSizeList: IPackageSizeList[] = useCommonStore((state: ICommonState) => state?.packageSizeList);
    const GetPackageSizeListAction = useCommonStore((state: ICommonState) => state?.GetPackageSizeListAction);
    const packagingList: IPackagingList[] = useCommonStore((state: ICommonState) => state?.packagingList);
    const GetPackagingListAction = useCommonStore((state: ICommonState) => state?.GetPackagingListAction);
    const truckTypeList: ITruckTypeList[] = useCommonStore((state: ICommonState) => state?.truckTypeList);
    const GetTruckTypeListAction = useCommonStore((state: ICommonState) => state?.GetTruckTypeListAction);
    const { country } = JSON.parse(SessionStorage.get('app-storage') ?? '{}');
    const originList = useCommonStore((state: ICommonState) => state?.originList);
    const destinationList = useCommonStore((state: ICommonState) => state?.destinationList);
    const getOriginListAction = useCommonStore((state: ICommonState) => state?.GetOriginListAction);
    const { promiseInProgress } = usePromiseTracker({ area: 'addVehicle-list-area' });
    const { id, waybillNo } = useOperationHook();
    const GetMarkenByIdAction = useCommonStore((state: ICommonState) => state?.GetMarkenByIdAction);
    const markenRequestList = useCommonStore((state: ICommonState) => state?.markenRequestList);
    const SaveMarkenAction = useCommonStore((state: ICommonState) => state?.SaveMarkenAction);


    const onCalculateHandler = useCallback(() => {
        const dryIceQuantity = form.getFieldValue('dryIceQuantity');
        const dryIceCharges = calculateDryIceCharge({ dryIceQuantity })
        form.setFieldValue('dryIceCharges', dryIceCharges);

        const transportationCharges = form.getFieldValue('transportationCharges') === undefined ? 0 : form.getFieldValue('transportationCharges');
        const airlineCharges = form.getFieldValue('airlineCharges') === undefined ? 0 : form.getFieldValue('airlineCharges');
        const fuelSecuritySurchargeCharge = form.getFieldValue('fuelSecuritySurchargeCharge') === undefined ? 0 : form.getFieldValue('fuelSecuritySurchargeCharge');
        const awbCharge = form.getFieldValue('awbCharge') === undefined ? 0 : form.getFieldValue('awbCharge');
        const otherCharge = form.getFieldValue('otherCharge') === undefined ? 0 : form.getFieldValue('otherCharge');
        const airportHandlingDo = form.getFieldValue('airportHandlingDo') === undefined ? 0 : form.getFieldValue('airportHandlingDo');
        const mofaicAttestationOfficialCharges = form.getFieldValue('mofaicAttestationOfficialCharges') === undefined ? 0 : form.getFieldValue('mofaicAttestationOfficialCharges');
        const calogiCharges = form.getFieldValue('calogiCharges') === undefined ? 0 : form.getFieldValue('calogiCharges');
        const customsClearence = form.getFieldValue('customsClearence') === undefined ? 0 : form.getFieldValue('customsClearence');
        const deliveryOrder = form.getFieldValue('deliveryOrder') === undefined ? 0 : form.getFieldValue('deliveryOrder');
        const bayan = form.getFieldValue('bayan') === undefined ? 0 : form.getFieldValue('bayan');
        const credoConditioningCharge = form.getFieldValue('credoConditioningCharge') === undefined ? 0 : form.getFieldValue('credoConditioningCharge');
        const dryIceReplinishment = form.getFieldValue('dryIceReplinishment') === undefined ? 0 : form.getFieldValue('dryIceReplinishment');

        const total = calculateTotalMarkenCharges({
            dryIceQuantity, dryIceCharges, transportationCharges,
            airlineCharges, fuelSecuritySurchargeCharge, awbCharge, otherCharge, airportHandlingDo,
            mofaicAttestationOfficialCharges, calogiCharges, customsClearence, deliveryOrder, bayan,
            credoConditioningCharge, dryIceReplinishment
        })

        form.setFieldValue('total', total);

    }, [form])
    const promiseAll = useCallback(() => async () => {
        (await Promise.allSettled([
            GetActivityListAction(),
            GetPackagingListAction(),
            GetTruckTypeListAction(),
            GetAirportCodeListAction(),
            GetShipmentTemperatureListAction()
        ]))
    }, [GetActivityListAction, GetPackagingListAction, GetTruckTypeListAction, GetAirportCodeListAction, GetShipmentTemperatureListAction])
    useEffect(() => {
        trackPromise(GetMarkenByIdAction(id), 'addVehicle-list-area')
        promiseAll()
    }, [])

    useEffect(() => {
        if (markenRequestList && Object?.keys(markenRequestList)?.length) {
            Object?.entries(markenRequestList)?.forEach(([key, value]) => {
                if (
                    key?.includes('ksaDeliveryTimeAtAirport') ||
                    key?.includes('ksaCollectionTimeFromAb') ||
                    key?.includes('mawbDate') ||
                    key?.includes('pickupTime') ||
                    key?.includes('deliveryTime') ||
                    key?.includes('scheduledDateOfCollection') ||
                    key?.includes('eta') ||
                    key?.includes('etd') ||
                    key?.includes('timeCleared') ||
                    key?.includes('timeReceived')) {
                    form.setFieldValue(key, convertMillisToDateMoment(value));
                }
                else if (key?.includes('orgCountryId') && value !== undefined) {
                    getOriginListAction(Number(value), 'origin');
                    form.setFieldValue(key, value);
                }
                else if (key?.includes('destCountryId') && value !== undefined) {
                    getOriginListAction(Number(value), 'destination');
                    form.setFieldValue(key, value);
                }
                else if (key?.includes('packagingId') && value !== undefined) {
                    GetPackageTempListAction(Number(value));
                    form.setFieldValue(key, value);
                }
                else if (key?.includes('packagingTempId') && value !== undefined) {
                    GetPackageSizeListAction(Number(value));
                    form.setFieldValue(key, value);
                }
                else if (key?.includes('month') && value !== undefined) {
                    const result = optionList.filter((item) => item.name === value);
                    form.setFieldValue('month', result[0]?.name);
                }
                else {
                    form.setFieldValue(key, value);
                }
            });

            // calculations:
            onCalculateHandler();
        }
    }, [GetPackageSizeListAction, GetPackageTempListAction, form, getOriginListAction, id, markenRequestList, onCalculateHandler]);


    const activityListMemo = useMemo(() => dropDownMapping(activityList, 'id', 'name'), [activityList]);
    const packagingListMemo = useMemo(() => dropDownMapping(packagingList, 'id', 'name'), [packagingList]);
    const truckTypeListMemo = useMemo(() => dropDownMapping(truckTypeList, 'id', 'name'), [truckTypeList]);
    const countryListMemo = useMemo(() => dropDownMapping(country, 'id', 'name'), [country]);
    const origincityListMemo = useMemo(() => dropDownMapping(originList, 'id', 'name'), [originList]);
    const destinationListMemo = useMemo(() => dropDownMapping(destinationList, 'id', 'name'), [destinationList]);
    const airportCodeListMemo = useMemo(() => dropDownMapping(airportCodeList, 'id', 'name'), [airportCodeList]);
    const shipmentTemperatureListMemo = useMemo(() => dropDownMapping(shipmentTemperatureList, 'id', 'name'), [shipmentTemperatureList]);
    const packageTempListMemo = useMemo(() => dropDownMapping(packageTempList, 'id', 'name'), [packageTempList]);
    const packageSizeListMemo = useMemo(() => dropDownMapping(packageSizeList, 'id', 'name'), [packageSizeList]);

    const onOriginCountryChangeHandler = (id: number) => {
        form.setFieldValue('orgCityId', '');
        getOriginListAction(id, 'origin')
    };
    const onDestinationCountryChangeHandler = (id: number) => {
        form.setFieldValue('destCityId', '');
        getOriginListAction(id, 'destination')
    };

    const onPackagingChangeHandler = (id: number) => {
        form.setFieldValue('packagingTempId', '');
        GetPackageTempListAction(id)
    };

    const onPackagingTempChangeHandler = (id: number) => {
        form.setFieldValue('packagingSizeValidationId', '');
        GetPackageSizeListAction(id)
    };

    const onMonthChangeHandler = (id: number) => {
        const result = optionList.filter((item) => item.id === id);
        form.setFieldValue('month', result[0]?.name);
    };
    const onSaveClicked = useCallback((values: IMarkenResponse): void => {
        const request = {
            ...values,
            bookingId: id,
            waybillNo: waybillNo,
            mawbDate: convertDateIntoMillis(values.mawbDate),
            ksaDeliveryTimeAtAirport: convertDateIntoMillis(values.ksaDeliveryTimeAtAirport),
            ksaCollectionTimeFromAb: convertDateIntoMillis(values.ksaCollectionTimeFromAb),
            pickupTime: convertDateIntoMillis(values.pickupTime),
            deliveryTime: convertDateIntoMillis(values.deliveryTime),
            scheduledDateOfCollection: convertDateIntoMillis(values.scheduledDateOfCollection),
            eta: convertDateIntoMillis(values.eta),
            etd: convertDateIntoMillis(values.etd),
            timeCleared: convertDateIntoMillis(values.timeCleared),
            timeReceived: convertDateIntoMillis(values.timeReceived),
        };
        trackPromise(SaveMarkenAction(request), 'addVehicle-list-area');
    }, [id, waybillNo, SaveMarkenAction])



    // const onCalculateHandler = useCallback(() => {
    //     const dryIceQuantity = form.getFieldValue('dryIceQuantity');
    //     const dryIceCharges = calculateDryIceCharge({ dryIceQuantity })
    //     form.setFieldValue('dryIceCharges', dryIceCharges);

    //     const transportationCharges = form.getFieldValue('transportationCharges') === undefined ? 0 : form.getFieldValue('transportationCharges');
    //     const airlineCharges = form.getFieldValue('airlineCharges') === undefined ? 0 : form.getFieldValue('airlineCharges');
    //     const fuelSecuritySurchargeCharge = form.getFieldValue('fuelSecuritySurchargeCharge') === undefined ? 0 : form.getFieldValue('fuelSecuritySurchargeCharge');
    //     const awbCharge = form.getFieldValue('awbCharge') === undefined ? 0 : form.getFieldValue('awbCharge');
    //     const otherCharge = form.getFieldValue('otherCharge') === undefined ? 0 : form.getFieldValue('otherCharge');
    //     const airportHandlingDo = form.getFieldValue('airportHandlingDo') === undefined ? 0 : form.getFieldValue('airportHandlingDo');
    //     const mofaicAttestationOfficialCharges = form.getFieldValue('mofaicAttestationOfficialCharges') === undefined ? 0 : form.getFieldValue('mofaicAttestationOfficialCharges');
    //     const calogiCharges = form.getFieldValue('calogiCharges') === undefined ? 0 : form.getFieldValue('calogiCharges');
    //     const customsClearence = form.getFieldValue('customsClearence') === undefined ? 0 : form.getFieldValue('customsClearence');
    //     const deliveryOrder = form.getFieldValue('deliveryOrder') === undefined ? 0 : form.getFieldValue('deliveryOrder');
    //     const bayan = form.getFieldValue('bayan') === undefined ? 0 : form.getFieldValue('bayan');
    //     const credoConditioningCharge = form.getFieldValue('credoConditioningCharge') === undefined ? 0 : form.getFieldValue('credoConditioningCharge');
    //     const dryIceReplinishment = form.getFieldValue('dryIceReplinishment') === undefined ? 0 : form.getFieldValue('dryIceReplinishment');

    //     const total = calculateTotalMarkenCharges({
    //         dryIceQuantity, dryIceCharges, transportationCharges,
    //         airlineCharges, fuelSecuritySurchargeCharge, awbCharge, otherCharge, airportHandlingDo,
    //         mofaicAttestationOfficialCharges, calogiCharges, customsClearence, deliveryOrder, bayan,
    //         credoConditioningCharge, dryIceReplinishment
    //     })

    //     form.setFieldValue('total', total);

    // }, [])


    return (
        <>
            <Row>
                <FileUploadComponent />
            </Row>
            <Divider />
            <LoadingModalComponent
                isLoading={promiseInProgress}
            >
                <Form form={form} name='addVehicle'
                    // initialValues={shipperInitial}
                    onFinish={onSaveClicked}
                    preserve={false}
                >
                    <Row justify={'start'}>


                        <Col span={4}>
                            <Form.Item
                                name="hawbNo"
                                label='Marken HAWB' labelCol={{ span: 24 }}
                                rules={[markenHAWBLengthValidation]}
                            >
                                <Input size="middle" className="select-width" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="maestroRouteLeg"
                                label='Maestro Route Leg' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="clientAccount"
                                label='Marken client name' labelCol={{ span: 24 }}
                            >
                                <Input size="middle" className="select-width" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="markenAC"
                                label='Marken A/C' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="shippingParty"
                                label='Shipping Party' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="orgCountryId"
                                label='Shipper Country' labelCol={{ span: 24 }}
                            >
                                <Select
                                    showSearch
                                    size="middle"
                                    allowClear
                                    className="select-width"
                                    onChange={onOriginCountryChangeHandler}
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)
                                            ?.toLocaleLowerCase()
                                            ?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {countryListMemo?.map((subCity) => (
                                        <React.Fragment key={subCity?.id}>
                                            <Select.Option value={subCity?.id}>
                                                {subCity?.value}
                                            </Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="orgCityId"
                                label='Shipper City' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)
                                            ?.toLocaleLowerCase()
                                            ?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {origincityListMemo?.map((country: any) => (
                                        <React.Fragment key={country?.id}>
                                            <Select.Option value={country?.id}>
                                                {country?.value}
                                            </Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="pickupTime"
                                label='Pickup Time' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="consigneeParty"
                                label='Consignee Party' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="destCountryId"
                                label='Consignee Country' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    allowClear
                                    showSearch
                                    onChange={onDestinationCountryChangeHandler}
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)
                                            ?.toLocaleLowerCase()
                                            ?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {countryListMemo?.map((country: any) => (
                                        <React.Fragment key={country?.id}>
                                            <Select.Option value={country?.id}>
                                                {country?.value}
                                            </Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>

                            <Form.Item
                                name="destCityId"
                                label='Consignee City' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)
                                            ?.toLocaleLowerCase()
                                            ?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {destinationListMemo?.map((country: any) => (
                                        <React.Fragment key={country?.id}>
                                            <Select.Option value={country?.id}>
                                                {country?.value}
                                            </Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="deliveryTime"
                                label='Delivery Time' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name='activityId'
                                label='Shipment Type' labelCol={{ span: 24 }}
                            // rules={[{ ...requiredValidation }]}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {activityListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="scheduledDateOfCollection"
                                label='Scheduled Date Of Collection' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    format={DD_MM_YYYY}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="month"
                                label='Month' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    onChange={onMonthChangeHandler}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {optionList?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="depatureAirportId"
                                label='Depature Airport' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {airportCodeListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="arrivalAirportId"
                                label='Arrival Airport' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {airportCodeListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="shipmentTemperatureId"
                                label='Shipment Temperature' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {shipmentTemperatureListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={4}>
                            <Form.Item
                                name='packagingId'
                                label='Packaging' labelCol={{ span: 24 }}
                            // rules={[{ ...requiredValidation }]}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    onChange={onPackagingChangeHandler}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {packagingListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="packagingTempId"
                                label='Packaging Temp' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    onChange={onPackagingTempChangeHandler}
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {packageTempListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="packagingSizeValidationId"
                                label='Packaging Size Validation' labelCol={{ span: 24 }}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {packageSizeListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="pieces"
                                label='Pieces' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="weight"
                                label='Weight(KG)' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name='truckTypeId'
                                label='Vehicle Type' labelCol={{ span: 24 }}
                            // rules={[{ ...requiredValidation }]}
                            >
                                <Select
                                    size="middle"
                                    className="select-width"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option!.children as unknown as string)?.toLocaleLowerCase()?.includes(input?.toLocaleLowerCase())
                                    }
                                >
                                    {truckTypeListMemo?.map((employee) => (
                                        <React.Fragment key={employee?.id}>
                                            <Select.Option value={employee?.id}>{employee?.value}</Select.Option>
                                        </React.Fragment>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="transportationCost"
                                label='Transportation Cost' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="transportationCharges"
                                label='Transportation Charges' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="airline"
                                label='Airline' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="airlineTripNo"
                                label='Airline Trip No' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="etd"
                                label='ETD' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="eta"
                                label='ETA' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="mawbNo"
                                label='MAWB Number' labelCol={{ span: 24 }}
                            >
                                <Input size="middle" className="select-width" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name='mawbDate'
                                label='MAWB Date' labelCol={{ span: 24 }}
                            // rules={[requiredValidation]} 
                            >
                                <DatePicker size="middle" className="select-width"
                                    format={DD_MM_YYYY}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="airlineCost"
                                label='Airline Cost' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="airlineCharges"
                                label='Airline Charges' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="fuelSecuritySurchargeCost"
                                label='fuel Security Surcharge Cost' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="fuelSecuritySurchargeCharge"
                                label='Fuel Security Surcharge Charge' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="awbCost"
                                label='Awb Cost' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="awbCharge"
                                label='Awb Charge' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="otherCharge"
                                label='Other Charge' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="airportHandlingDo"
                                label='Airport Handling (DO)' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="mofaicAttestationOfficialCharges"
                                label='MOFAIC Attestation official charges' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="calogiCharges"
                                label='Calogi Charges (custom duty official charges)' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="customsClearence"
                                label='Customs Clearence' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="timeReceived"
                                label='Time Received' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="timeCleared"
                                label='Time Cleared' labelCol={{ span: 24 }}
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="deliveryOrder"
                                label='Delivery Order (BOE official charges)' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="bayan"
                                label='Bayan' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="credoConditioningCharge"
                                label='Credo Conditioning Charge' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}

                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="dryIceCost"
                                label='Dry Ice Cost' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="dryIceQuantity"
                                label='Dry ICE KG' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    onChange={onCalculateHandler}
                                    size="middle"
                                    className="select-width"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="dryIceReplinishment"
                                label='Dry Ice Replinishment' labelCol={{ span: 24 }}
                                rules={[{ ...numericValidation }]}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="dryIceCharges"
                                label='Dry Ice Charges' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    disabled
                                    onChange={onCalculateHandler}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="total"
                                label='Total' labelCol={{ span: 24 }}
                            >
                                <Input
                                    size="middle"
                                    className="select-width"
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="shipperLab"
                                label='Shipper lab' labelCol={{ span: 24 }}
                            >
                                <Input size="middle" className="select-width" />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="recieverLab"
                                label='Reciever lab' labelCol={{ span: 24 }}
                            >
                                <Input size="middle" className="select-width" />
                            </Form.Item>
                        </Col>


                        <Col span={4}>
                            <Form.Item
                                name='ksaDeliveryTimeAtAirport'
                                label='KSA Delivery Time At Airport' labelCol={{ span: 24 }}
                            // rules={[requiredValidation]} 
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name='ksaCollectionTimeFromAb'
                                label='KSA Collection Time From Ab' labelCol={{ span: 24 }}
                            // rules={[requiredValidation]} 
                            >
                                <DatePicker size="middle" className="select-width"
                                    showTime
                                    format={DD_MM_YYYY_HH_mm}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item
                                name="remarks"
                                label='Remarks' labelCol={{ span: 24 }}
                            >
                                <Input.TextArea
                                    rows={4}
                                    cols={4}
                                    size="middle" className="select-width"
                                ></Input.TextArea>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify='end'>
                        <Button data-args={'save'} type='primary' htmlType="submit" style={{ width: '10em', height: '2.5em' }}
                        // disabled={state?.isDelivered}
                        >save</Button>
                    </Row>
                </Form>
            </LoadingModalComponent>
        </>
    );
}

MarkenFormComponent.propTypes = {};

export default MarkenFormComponent;
